<template>
  <div class="user-api-devices">
    <div class="d-flex justify-content-between align-items-center mb-2">
      <label class="form-label mb-0">Dispositivos liberados</label>
      <!-- Só dá para recolher enquanto não há nada liberado: com itens o bloco fica sempre aberto. -->
      <button
        v-if="expanded && selected.length === 0"
        type="button"
        class="btn btn-link btn-sm p-0 text-muted"
        @click="expanded = false"
      >
        Ocultar
      </button>
    </div>

    <div v-if="!expanded">
      <button type="button" class="btn btn-sm btn-outline-primary" @click="expanded = true">
        <i class="bx bx-plus me-1"></i> Liberar dispositivos
      </button>
    </div>

    <div v-else class="card border-0 bg-light p-3">
      <p class="text-muted small mb-3">
        Dispositivos que este usuário poderá acessar (somente leitura).
        <strong>Mapa</strong>: aparece na aba de dispositivos do mapa, sem gerar relatórios nem
        cadastrar alertas.
        <template v-if="apiEnabled"><strong>API</strong>: consulta via x-api-key.</template>
        <template v-else>Marque "Uso da api" para também poder liberar via API.</template>
        O acesso não aparece como compartilhado e a propriedade do dono não é exibida.
      </p>

      <div v-if="!userId" class="alert alert-warning py-2 mb-0">
        Salve o usuário primeiro para gerenciar os dispositivos liberados.
      </div>

      <template v-else>
        <div class="mb-3">
          <label class="form-label">Adicionar dispositivos</label>
          <VueMultiselect
            v-model="selected"
            :options="options"
            :multiple="true"
            :show-labels="false"
            :loading="loadingOptions"
            placeholder="Buscar por nome ou código QR"
            label="display"
            track-by="qrCodeId"
            @search-change="onSearchChange"
            @select="onSelect"
            :internal-search="false"
          >
            <template v-slot:noResult>Nenhum dispositivo encontrado.</template>
            <template v-slot:noOptions>Digite para buscar.</template>
          </VueMultiselect>
        </div>

        <div v-if="selected.length > 0">
          <div class="bulk-bar mb-2">
            <label class="form-label mb-0">Nome fictício (opcional)</label>
            <!-- Marca/desmarca o canal em todas as linhas: se todas já estão marcadas,
                 desmarca; senão, marca todas. -->
            <div class="bulk-actions">
              <small class="text-muted">Todos:</small>
              <button
                v-if="apiEnabled"
                type="button"
                class="btn btn-sm channel-btn"
                :class="allChecked('apiAccess') ? 'btn-primary' : 'btn-outline-secondary'"
                :title="allChecked('apiAccess') ? 'Desmarcar API em todos' : 'Marcar API em todos'"
                @click="toggleAll('apiAccess')"
              >
                <i class="bx" :class="allChecked('apiAccess') ? 'bx-check' : 'bx-x'"></i> API
              </button>
              <button
                type="button"
                class="btn btn-sm channel-btn"
                :class="allChecked('mapAccess') ? 'btn-primary' : 'btn-outline-secondary'"
                :title="allChecked('mapAccess') ? 'Desmarcar Mapa em todos' : 'Marcar Mapa em todos'"
                @click="toggleAll('mapAccess')"
              >
                <i class="bx" :class="allChecked('mapAccess') ? 'bx-check' : 'bx-x'"></i> Mapa
              </button>
            </div>
          </div>

          <div v-for="item in selected" :key="item.qrCodeId" class="device-row">
            <div class="device-tag">
              <span class="device-tag-name">{{ item.nomeDispositivo || "(sem nome)" }}</span>
              <span class="device-tag-code">{{ item.codigoQR }}</span>
            </div>
            <input
              type="text"
              class="form-control device-alias"
              v-model="item.alias"
              maxlength="120"
              placeholder="Nome fictício (vazio = nome original)"
            />
            <div
              class="device-channels"
              :class="{ 'device-channels-empty': !item.apiAccess && !item.mapAccess }"
              :title="!item.apiAccess && !item.mapAccess ? 'Sem canal marcado: o acesso será removido ao salvar' : ''"
            >
              <button
                v-if="apiEnabled"
                type="button"
                class="btn btn-sm channel-btn"
                :class="item.apiAccess ? 'btn-primary' : 'btn-outline-secondary'"
                :aria-pressed="item.apiAccess ? 'true' : 'false'"
                title="Liberar consulta via API"
                @click="item.apiAccess = !item.apiAccess"
              >
                <i class="bx" :class="item.apiAccess ? 'bx-check' : 'bx-x'"></i> API
              </button>
              <!-- API já liberada antes de desmarcarem "Uso da api": fica guardada (volta a valer
                   se reativarem), mas sem botão para não parecer uma segunda chave de API. -->
              <span
                v-else-if="item.apiAccess"
                class="badge bg-label-secondary channel-badge"
                title="Liberado via API, mas sem efeito enquanto Uso da api estiver desmarcado"
              >API inativa</span>
              <button
                type="button"
                class="btn btn-sm channel-btn"
                :class="item.mapAccess ? 'btn-primary' : 'btn-outline-secondary'"
                :aria-pressed="item.mapAccess ? 'true' : 'false'"
                title="Mostrar na aba de dispositivos do mapa (sem relatório e alerta)"
                @click="item.mapAccess = !item.mapAccess"
              >
                <i class="bx" :class="item.mapAccess ? 'bx-check' : 'bx-x'"></i> Mapa
              </button>
            </div>
            <button
              type="button"
              class="btn btn-outline-danger device-remove"
              @click="removeRow(item)"
              title="Remover"
            >
              <i class="bx bx-x"></i>
            </button>
          </div>
        </div>

        <div v-if="rejected.length > 0" class="alert alert-warning mt-3 mb-0" role="alert">
          <div class="d-flex justify-content-between align-items-start mb-1">
            <strong>
              <i class="bx bx-error-circle me-1"></i>
              {{ rejected.length }} {{ rejected.length === 1 ? 'dispositivo não foi salvo' : 'dispositivos não foram salvos' }}
            </strong>
            <button type="button" class="btn-close btn-sm" @click="rejected = []" aria-label="Fechar"></button>
          </div>
          <ul class="mb-0 ps-3 small">
            <li v-for="item in rejected" :key="'rej-' + item.qrCodeId">
              <span class="text-muted">QR Code #{{ item.qrCodeId }}:</span> {{ item.reason }}
            </li>
          </ul>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import VueMultiselect from "vue-multiselect";

export default {
  name: "UserApiDevices",
  components: { VueMultiselect },
  props: {
    userId: { type: [Number, String], default: null },
    // Reflete o "Uso da api" do usuário: sem ele o canal API não tem efeito.
    apiEnabled: { type: Boolean, default: true },
  },
  data() {
    return {
      selected: [],
      options: [],
      loadingOptions: false,
      searchDebounce: null,
      rejected: [],
      // Recolhido por padrão; abre sozinho quando o usuário já tem algo liberado.
      expanded: false,
      // Só salva depois de ler os acessos atuais: o PUT substitui a lista inteira, então
      // salvar com a leitura falha/pendente apagaria as liberações do usuário.
      loaded: false,
    };
  },
  watch: {
    userId: {
      immediate: true,
      handler(val) {
        if (val) {
          this.loadAccesses();
          this.loadOptions("");
        } else {
          this.selected = [];
          this.options = [];
          this.rejected = [];
          this.expanded = false;
          this.loaded = false;
        }
      },
    },
  },
  methods: {
    decorate(item) {
      return {
        qrCodeId: item.qrCodeId,
        codigoQR: item.codigoQR,
        nomeDispositivo: item.nomeDispositivo,
        alias: item.alias || "",
        apiAccess: !!item.apiAccess,
        mapAccess: !!item.mapAccess,
        display:
          (item.nomeDispositivo || "(sem nome)") + " — " + item.codigoQR,
      };
    },
    loadAccesses() {
      this.loaded = false;
      this.urlPost("/AdminUserApiDeviceAccess/Read", { userId: Number(this.userId) })
        .then((res) => {
          this.selected = (res.data.itens || []).map((i) => this.decorate(i));
          this.loaded = true;
          if (this.selected.length > 0) this.expanded = true;
        })
        .catch((e) => {
          console.error(e);
        });
    },
    loadOptions(search) {
      this.loadingOptions = true;
      this.urlPost("/AdminUserApiDeviceAccess/Available", {
        search: search || "",
        limit: 100,
        excludeUserId: Number(this.userId),
      })
        .then((res) => {
          const selectedIds = new Set(this.selected.map((s) => s.qrCodeId));
          this.options = (res.data.itens || [])
            .filter((i) => !selectedIds.has(i.qrCodeId))
            .map((i) => this.decorate(i));
        })
        .finally(() => {
          this.loadingOptions = false;
        });
    },
    onSearchChange(query) {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        this.loadOptions(query);
      }, 300);
    },
    // Dispositivo recém-adicionado entra liberado no Mapa (API é opt-in), para nunca
    // aparecer com os dois canais desmarcados.
    onSelect(item) {
      if (!item.apiAccess && !item.mapAccess) item.mapAccess = true;
    },
    allChecked(field) {
      return this.selected.length > 0 && this.selected.every((s) => s[field]);
    },
    toggleAll(field) {
      const value = !this.allChecked(field);
      this.selected.forEach((s) => {
        s[field] = value;
      });
    },
    removeRow(item) {
      this.selected = this.selected.filter((s) => s.qrCodeId !== item.qrCodeId);
    },
    save() {
      if (!this.userId) return Promise.resolve();
      if (!this.loaded) return Promise.reject(new Error("Acessos ainda não carregados; nada foi salvo."));
      this.rejected = [];
      const payload = {
        userId: Number(this.userId),
        accesses: this.selected.map((s) => ({
          qrCodeId: s.qrCodeId,
          alias: s.alias && s.alias.trim() ? s.alias.trim() : null,
          apiAccess: !!s.apiAccess,
          mapAccess: !!s.mapAccess,
        })),
      };
      return this.urlPut("/AdminUserApiDeviceAccess", payload).then((res) => {
        // O backend descarta itens sem canal marcado; espelha isso na lista.
        this.selected = this.selected.filter((s) => s.apiAccess || s.mapAccess);
        const data = res && res.data ? res.data : {};
        const rejected = Array.isArray(data.rejected) ? data.rejected : [];
        if (rejected.length > 0) {
          this.rejected = rejected;
          const rejectedIds = new Set(rejected.map((r) => r.qrCodeId));
          this.selected = this.selected.filter((s) => !rejectedIds.has(s.qrCodeId));
        }
        return res;
      });
    },
  },
};
</script>

<style scoped>
/* Desktop: dispositivo | nome fictício | canais | remover, numa linha só. */
.device-row {
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr) auto 48px;
  grid-template-areas: "tag alias channels remove";
  gap: 8px;
  margin-bottom: 8px;
}
.device-row > * {
  min-height: 48px;
}
.device-tag {
  grid-area: tag;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  padding: 4px 12px;
  background-color: #fff;
  border: 1px solid #d9dee3;
  border-radius: 6px;
  line-height: 1.15;
  overflow: hidden;
}
.device-tag-name,
.device-tag-code {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.device-tag-name {
  font-size: 0.85rem;
  font-weight: 500;
  color: #566a7f;
}
.device-tag-code {
  font-size: 0.7rem;
  color: #a1acb8;
  margin-top: 2px;
  letter-spacing: 0.3px;
}
.device-alias {
  grid-area: alias;
  min-width: 0;
}
.device-channels {
  grid-area: channels;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 6px;
  background-color: #fff;
  border: 1px solid #d9dee3;
  border-radius: 6px;
}
.device-channels-empty {
  border-color: #ffab00;
}
.device-remove {
  grid-area: remove;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.device-remove i {
  font-size: 1.1rem;
}
.channel-badge {
  white-space: nowrap;
}
.channel-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-width: 72px;
  white-space: nowrap;
}
.bulk-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.bulk-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Celular: cada dispositivo vira um bloco (nome + remover, nome fictício, canais em
   botões de largura igual). */
@media (max-width: 768px) {
  .device-row {
    grid-template-columns: minmax(0, 1fr) 48px;
    grid-template-areas:
      "tag remove"
      "alias alias"
      "channels channels";
    padding: 10px;
    margin-bottom: 10px;
    background-color: #fff;
    border: 1px solid #d9dee3;
    border-radius: 8px;
  }
  .device-row > * {
    min-height: 42px;
  }
  .device-tag {
    border: 0;
    padding: 0 4px;
  }
  .device-channels {
    border: 0;
    padding: 0;
    background: transparent;
  }
  .device-channels .channel-btn,
  .device-channels .channel-badge {
    flex: 1 1 0;
    min-height: 38px;
  }
  .device-channels-empty .channel-btn {
    border-color: #ffab00;
  }
  .bulk-actions {
    width: 100%;
  }
  .bulk-actions .channel-btn {
    flex: 1 1 0;
  }
}
</style>
